import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, Wrench, Clock, ChevronDown, ChevronUp,
  ExternalLink, ArrowLeft, RefreshCw, Globe, Server, MessageCircle, Shield,
  Wifi, Database, Zap, Activity, Monitor, Mail, Bell, History, TrendingUp,
  BarChart3, Circle, Layers
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  operational: { label: 'All Systems Operational', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', dot: 'bg-emerald-500', barColor: 'bg-emerald-500' },
  degraded: { label: 'Degraded Performance', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', dot: 'bg-amber-500', barColor: 'bg-amber-500' },
  major_outage: { label: 'Major Outage', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', dot: 'bg-red-500', barColor: 'bg-red-500' },
  maintenance: { label: 'Under Maintenance', icon: Wrench, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', dot: 'bg-cyan-500', barColor: 'bg-cyan-500' },
};

const SEVERITY_COLORS = {
  minor: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  major: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  critical: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const INCIDENT_STATUS = {
  investigating: { label: 'Investigating', color: 'text-red-400', bg: 'bg-red-500/10' },
  identified: { label: 'Identified', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  monitoring: { label: 'Monitoring', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  resolved: { label: 'Resolved', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
};

const SERVICES = [
  { id: 'website', name: 'Website', icon: Globe, desc: 'Public-facing website' },
  { id: 'staff_portal', name: 'Staff Portal', icon: Shield, desc: 'Internal staff dashboard' },
  { id: 'live_chat', name: 'Live Chat', icon: MessageCircle, desc: 'Customer support chat' },
  { id: 'api', name: 'API Services', icon: Server, desc: 'Backend infrastructure' },
  { id: 'database', name: 'Database', icon: Database, desc: 'Data storage systems' },
  { id: 'cdn', name: 'CDN & Assets', icon: Layers, desc: 'Content delivery & media' },
];

const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtShort = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [uptimeData, setUptimeData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/status`);
      setData(r.data);
      setLastRefresh(new Date());
      // Generate mock uptime data for last 30 days
      const uptime = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const hasIncident = r.data.resolved_incidents?.some(inc => {
          const incDate = new Date(inc.resolved_at || inc.created_at);
          return incDate.toDateString() === date.toDateString();
        });
        uptime.push({
          date: date.toISOString(),
          status: hasIncident ? 'incident' : r.data.overall_status === 'maintenance' && i === 0 ? 'maintenance' : 'operational'
        });
      }
      setUptimeData(uptime);
    } catch {}
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const status = STATUS_CONFIG[data.overall_status] || STATUS_CONFIG.operational;
  const StatusIcon = status.icon;
  const operationalDays = uptimeData.filter(d => d.status === 'operational').length;
  const uptimePercent = uptimeData.length > 0 ? Math.round((operationalDays / uptimeData.length) * 1000) / 10 : 100;

  const getServiceStatus = (serviceId) => {
    if (data.overall_status === 'maintenance') return serviceId === 'api' || serviceId === 'database' ? 'operational' : 'maintenance';
    const hasIncident = data.active_incidents?.some(inc => inc.affected_services?.includes(serviceId));
    if (hasIncident) return 'degraded';
    return 'operational';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="status-page">
      {/* Header */}
      <div className="border-b border-slate-800/50 bg-slate-900/30 sticky top-0 z-10 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-mono tracking-wide">status.starnightholdings.uk</span>
              <span className="text-slate-700">|</span>
              <h1 className="text-sm font-medium text-slate-300">System Status</h1>
            </div>
            <div className="w-px h-4 bg-slate-800" />
            <button onClick={fetchData} disabled={isRefreshing}
              className="flex items-center gap-1.5 text-slate-500 hover:text-white text-xs transition-colors" data-testid="refresh-status-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-[10px] px-2 py-1 rounded-full border transition-all ${autoRefresh ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-slate-800/50 text-slate-500 border-slate-700'}`}
              data-testid="auto-refresh-toggle">
              {autoRefresh ? 'Auto' : 'Manual'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Overall Status Banner */}
        <div className={`${status.bg} border ${status.border} rounded-2xl p-6 mb-8 relative overflow-hidden`} data-testid="overall-status">
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-[80px] opacity-20" style={{ background: status.color.includes('emerald') ? '#10b981' : status.color.includes('amber') ? '#f59e0b' : status.color.includes('red') ? '#ef4444' : '#06b6d4' }} />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl ${status.bg} border ${status.border} flex items-center justify-center`}>
                <StatusIcon className={`w-7 h-7 ${status.color}`} />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${status.color}`}>{status.label}</h2>
                <p className="text-slate-500 text-sm mt-0.5">
                  Updated {timeAgo(lastRefresh.toISOString())} · {uptimePercent}% uptime last 30 days
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${status.dot} animate-pulse`} />
              <span className={`text-xs font-medium ${status.color}`}>Live</span>
            </div>
          </div>
        </div>

        {/* 30-Day Uptime Bar */}
        <div className="mb-8" data-testid="uptime-bar">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5 text-slate-500" /> 30-Day Uptime
            </h3>
            <span className="text-emerald-400 text-sm font-bold">{uptimePercent}%</span>
          </div>
          <div className="flex gap-[3px] h-8 items-end">
            {uptimeData.map((day, i) => {
              const s = day.status;
              const color = s === 'operational' ? 'bg-emerald-500' : s === 'maintenance' ? 'bg-cyan-500' : 'bg-amber-500';
              const h = s === 'operational' ? 'h-full' : s === 'maintenance' ? 'h-3/4' : 'h-1/2';
              return (
                <div key={i} className="flex-1 group relative cursor-pointer">
                  <div className={`${color} ${h} rounded-sm opacity-70 group-hover:opacity-100 transition-all min-h-[4px]`} />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-xl">
                      <p className="text-white text-[10px] font-medium">{new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                      <p className={`text-[10px] capitalize ${s === 'operational' ? 'text-emerald-400' : s === 'maintenance' ? 'text-cyan-400' : 'text-amber-400'}`}>{s}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-slate-600 text-[10px]">30 days ago</span>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Operational</span>
              <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-sm bg-cyan-500" /> Maintenance</span>
              <span className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Incident</span>
            </div>
            <span className="text-slate-600 text-[10px]">Today</span>
          </div>
        </div>

        {/* Service Status Grid */}
        <div className="mb-8" data-testid="services-grid">
          <h3 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium mb-4 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-slate-500" /> Services
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SERVICES.map(service => {
              const svcStatus = getServiceStatus(service.id);
              const cfg = STATUS_CONFIG[svcStatus] || STATUS_CONFIG.operational;
              const SvcIcon = service.icon;
              return (
                <div key={service.id} className={`${cfg.bg} border ${cfg.border} rounded-xl p-4 transition-all hover:border-opacity-60`}
                  data-testid={`service-${service.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <SvcIcon className={`w-4 h-4 ${cfg.color}`} />
                      <span className="text-white text-sm font-medium">{service.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                      <span className={`text-[10px] font-medium ${cfg.color}`}>{STATUS_CONFIG[svcStatus]?.label?.split(' ').pop()}</span>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs">{service.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active Incidents */}
        {data.active_incidents?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium mb-4 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Active Incidents
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 font-bold">{data.active_incidents.length}</span>
            </h3>
            <div className="space-y-3">
              {data.active_incidents.map((inc, i) => (
                <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid={`incident-${i}`}>
                  <button onClick={() => setExpandedIncident(expandedIncident === i ? null : i)}
                    className="w-full p-4 flex items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${inc.severity === 'critical' ? 'bg-red-500' : inc.severity === 'major' ? 'bg-orange-500' : 'bg-amber-500'} animate-pulse`} />
                      <div>
                        <p className="text-white font-medium text-sm">{inc.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-px rounded-full font-semibold uppercase ${SEVERITY_COLORS[inc.severity] || ''}`}>{inc.severity}</span>
                          <span className={`text-[10px] px-1.5 py-px rounded-full font-medium ${INCIDENT_STATUS[inc.status]?.bg} ${INCIDENT_STATUS[inc.status]?.color}`}>{INCIDENT_STATUS[inc.status]?.label}</span>
                          <span className="text-slate-600 text-[10px]">{timeAgo(inc.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {expandedIncident === i ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </button>
                  {expandedIncident === i && (
                    <div className="px-4 pb-4 border-t border-slate-800 pt-3" style={{ animation: 'fadeIn 0.2s ease-out' }}>
                      <p className="text-slate-400 text-sm mb-3">{inc.description}</p>
                      {inc.affected_services?.length > 0 && (
                        <div className="flex gap-1.5 mb-3 flex-wrap">
                          {inc.affected_services.map(s => (
                            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700 flex items-center gap-1">
                              <Server className="w-2.5 h-2.5" />{s}
                            </span>
                          ))}
                        </div>
                      )}
                      {inc.updates?.length > 0 && (
                        <div className="space-y-3 border-l-2 border-slate-700 pl-4 ml-1">
                          {inc.updates.map((u, j) => (
                            <div key={j} className="relative">
                              <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-slate-700 border-2 border-slate-900" />
                              <p className="text-[10px] text-slate-500 mb-0.5">{fmt(u.updated_at)} · <span className={INCIDENT_STATUS[u.status]?.color}>{INCIDENT_STATUS[u.status]?.label}</span></p>
                              <p className="text-sm text-slate-300">{u.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scheduled Maintenance */}
        {data.scheduled_maintenance?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium mb-4 flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5 text-cyan-400" /> Upcoming Maintenance
            </h3>
            <div className="space-y-3">
              {data.scheduled_maintenance.map((m, i) => (
                <div key={i} className="bg-cyan-500/5 border border-cyan-500/15 rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-full blur-[40px]" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-cyan-400" />
                        <p className="text-white font-medium text-sm">{m.title}</p>
                      </div>
                      {m.full_lockdown && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 font-semibold">FULL LOCKDOWN</span>}
                    </div>
                    {m.description && <p className="text-slate-400 text-sm mb-3 ml-6">{m.description}</p>}
                    <div className="flex items-center gap-4 ml-6">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-slate-400 text-xs">{fmtShort(m.scheduled_start)}</span>
                      </div>
                      <span className="text-slate-700">-</span>
                      <span className="text-slate-400 text-xs">{fmtShort(m.scheduled_end)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Resolved */}
        {data.resolved_incidents?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium mb-4 flex items-center gap-2">
              <History className="w-3.5 h-3.5 text-emerald-400" /> Recently Resolved
            </h3>
            <div className="space-y-2">
              {data.resolved_incidents.map((inc, i) => (
                <div key={i} className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-4 flex items-center justify-between group hover:border-emerald-500/20 transition-all">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <div>
                      <p className="text-slate-300 text-sm font-medium">{inc.title}</p>
                      <p className="text-slate-600 text-[10px] mt-0.5">Resolved {timeAgo(inc.resolved_at)}</p>
                    </div>
                  </div>
                  <span className="text-emerald-400/40 text-[10px] font-medium">Resolved</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="border-t border-slate-800/50 pt-6 mt-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-slate-600">
              <a href="https://discord.gg/2PFMA3hKjW" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs hover:text-slate-400 transition-colors">
                <MessageCircle className="w-3.5 h-3.5" /> Discord
              </a>
              <a href="mailto:help@starnightholdings.uk" className="flex items-center gap-1.5 text-xs hover:text-slate-400 transition-colors">
                <Mail className="w-3.5 h-3.5" /> Contact
              </a>
              <Link to="/" className="flex items-center gap-1.5 text-xs hover:text-slate-400 transition-colors">
                <Globe className="w-3.5 h-3.5" /> starnightholdings.uk
              </Link>
            </div>
            <p className="text-slate-700 text-[10px]">&copy; {new Date().getFullYear()} Starnight Holdings. All rights reserved.</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default StatusPage;
